import React from "react";
import { GoogleMaps } from "components/generic/GoogleMaps";
import { OpeningTimesItem } from "generated/types/types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "redux/store/store";

export type Props = {
    serviceCentreDetails?: ServiceCentreDetailsCardData;
    distance?: string;
    countryCode?: "DK" | "GB";
};

export type ServiceCentreDetailsCardData = {
    name: string;
    addressLines: string[];
    openingTimes: OpeningTimesItem[];
    latitude: number;
    longitude: number;
};

const countryToLocale: Record<"DK" | "GB", string> = {
    DK: "da-DK",
    GB: "en-GB",
};

const openingDayTranslationKeys: Record<string, string> = {
    Monday: "opening_times_monday",
    Mon: "opening_times_monday",
    Tuesday: "opening_times_tuesday",
    Tue: "opening_times_tuesday",
    Wednesday: "opening_times_wednesday",
    Wed: "opening_times_wednesday",
    Thursday: "opening_times_thursday",
    Thu: "opening_times_thursday",
    Friday: "opening_times_friday",
    Fri: "opening_times_friday",
    Saturday: "opening_times_saturday",
    Sat: "opening_times_saturday",
    Sunday: "opening_times_sunday",
    Sun: "opening_times_sunday",
};

const openingHoursTranslationKeys: Record<string, string> = {
    Closed: "opening_times_closed",
};

function countryFromLngParam(lngParam?: string): "DK" | "GB" | undefined {
    if (!lngParam) return undefined;
    const v = lngParam.toLowerCase();
    if (v === "dk") return "DK";
    if (v === "en") return "GB";
    return undefined;
}

export const ServiceCentreDetailsCard: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation();
    const storeCountry = useSelector((s: RootState) => s.country.countryCode) as "DK" | "GB" | string;

    const urlLng =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search).get("lng") ?? undefined
            : undefined;

    const urlCountry = countryFromLngParam(urlLng);
    const effectiveCountry: "DK" | "GB" =
        urlCountry ??
        (props.countryCode as "DK" | "GB" | undefined) ??
        (storeCountry === "DK" ? "DK" : "GB");

    const language = countryToLocale[effectiveCountry];
    const translationLanguage = effectiveCountry === "DK" ? "dk" : "en";

    const serviceCentreDetails = props.serviceCentreDetails;

    if (!serviceCentreDetails) {
        return <></>;
    }

    const addressLines = serviceCentreDetails.addressLines;

    return (
        <div className="service-centre-card">
            <div className="row map-row">
                <div className="col-12 map">
                    <GoogleMaps
                        address="here"
                        lat={serviceCentreDetails.latitude}
                        lng={serviceCentreDetails.longitude}
                        zoomLevel={14}
                        language={language}
                        region={effectiveCountry}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-12 service-centre-heading">
                    <h4>
                        {serviceCentreDetails.name} - {serviceCentreDetails.addressLines[1]}
                    </h4>
                    <span>{props.distance}</span>
                </div>
                <div className="col-6 address">
                    <h5>{t("service_center_address")}</h5>
                    {addressLines.map(line => (
                        <span key={line}>{line}</span>
                    ))}
                </div>
                <div className="col-6 open-hrs">
                    <h5>{t("service_center_opening_hours")}</h5>
                    <span>
                        {serviceCentreDetails.openingTimes?.map(x => (
                            <span key={x.day + x.hours}>
                                {t(openingDayTranslationKeys[x.day] ?? x.day, {
                                    lng: translationLanguage,
                                })}
                                {" : "}
                                {t(openingHoursTranslationKeys[x.hours] ?? x.hours, {
                                    lng: translationLanguage,
                                })}
                            </span>
                        ))}
                    </span>
                </div>
            </div>
        </div>
    );
};